import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useTranslations } from 'hooks/useTranslations';
import { formatDate } from 'utils/date';

interface IAttachmentTableCard {
  data: ITableDataSource;
  handleView: (arg: string) => void;
  handleDownload: (arg: string, name: string) => void;
  showView: boolean;
  shortDateFormat: string;
}

export const AttachmentTableCard = ({
  data,
  shortDateFormat,
  showView,
  handleView,
  handleDownload,
}: IAttachmentTableCard) => {
  const { t } = useTranslations();

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: colors.gray100,
      }}
      columnSpacing={2}
    >
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.attachments.filename.column')}
        </Typography>
        <Typography variant="body2">{data.name}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.attachments.type.column')}
        </Typography>
        <Typography variant="body2">{data.type}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.attachments.comment.column')}
        </Typography>
        <Typography variant="body2">{data.comment}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.attachments.createdDate.column')}
        </Typography>
        <Typography variant="body2">
          {data.createdDate ? formatDate(data.createdDate, shortDateFormat) : '-'}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntity.licenceDetails.attachments.addedBy.column')}
        </Typography>
        <Typography variant="body2">{data.addedBy}</Typography>
      </Grid>

      <Grid
        container
        size={{ xs: 12 }}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          },
          px: 2,
          pb: 2,
          mt: 1,
          pt: 2,
        }}
      >
        <Grid size={{ xs: 12, sm: 6 }} sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('legalEntity.licenceDetails.attachments.actions.column')}:{' '}
          </Typography>
        </Grid>
        <Grid container size={{ xs: 12, sm: 6 }} columnSpacing={2}>
          <Grid size={{ xs: 6 }}>
            <Button
              label={t('legalEntity.licenceDetails.attachments.download.column')}
              variant={EButtonVariants.outlined}
              onClick={() => handleDownload(data.id as string, data.name)}
              size={EButtonSizes.small}
              fullWidth
            />
          </Grid>
          {showView && (
            <Grid size={{ xs: 6 }}>
              <Button
                label={t('legalEntity.licenceDetails.attachments.view.column')}
                variant={EButtonVariants.outlined}
                onClick={() => handleView(data.id as string)}
                size={EButtonSizes.small}
                fullWidth
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
